import { Flex, FormControl, FormLabel, Input, Text } from '@chakra-ui/react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { TypesAllergy } from '@/modules/medicalRecord/domain/models/AllergyPatientMedicalRecord'
import { ResponsibleMedicalRecord } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecord'
import { ResponsibleMedicalRecordReview } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordReview'
import { ResponsibleMedicalRecordView } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordView'
import { ReviewResponsibleMedicalRecordForm } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/UpdateMedicalRecordDTO'
import { CustomSelect } from '@/ui/components/CustomSelect'

import { MedicalRecordReviewType } from '../../../../constants/MedicalRecordReviewType'
import { ReviewDataCheckboxArrayValue } from '../components/ReviewDataCheckbox'
import { useReviewFormRepeatedMultipleArrayValues } from '../components/ReviewFormRepeatedValue'
import ReviewStepWrapper from '../components/ReviewStepWrapper'

export function AllergiesReviewStep({
  data,
  mode
}:
  | {
      data: ResponsibleMedicalRecordView
      mode: typeof MedicalRecordReviewType.enum.VIEW
    }
  | {
      data: ResponsibleMedicalRecordReview
      mode: typeof MedicalRecordReviewType.enum.REVIEW
    }) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()

  useReviewFormRepeatedMultipleArrayValues({
    disabled: mode === MedicalRecordReviewType.enum.VIEW,
    form,
    formProp: 'allergies',
    serverValue: data.registerPatient.allergies,
    responsibleValue: data.sendResponsible.allergies,
    comparison: (a, b) =>
      !a
        ? false
        : !b
          ? false
          : a.name === b.name && a.typeAllergy === b.typeAllergy
  })

  return (
    <Flex justifyContent={'space-between'} gap={'6'} overflowX={'clip'}>
      <AllergiesReviewPart
        sourceData={data.sendResponsible}
        comparisonData={
          mode === 'REVIEW' ? data.registerPatient : form.getValues()
        }
        mode={mode}
      />
      <AllergiesReviewPart
        sourceData={data.registerPatient}
        comparisonData={
          mode === 'REVIEW' ? data.sendResponsible : form.getValues()
        }
        mode={mode}
      />
    </Flex>
  )
}

function AllergiesReviewPart({
  sourceData,
  comparisonData,
  mode
}: {
  sourceData: ResponsibleMedicalRecord
  comparisonData: ReviewResponsibleMedicalRecordForm | ResponsibleMedicalRecord
  mode: MedicalRecordReviewType
}) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()
  const [allergies] = form.watch(['allergies'])
  const allergiesFieldArray = useFieldArray({
    control: form.control,
    name: 'allergies'
  })

  return (
    <ReviewStepWrapper>
      {sourceData.allergies.length === 0 ? (
        <Flex>
          <Text>Sem opções disponíveis</Text>
        </Flex>
      ) : (
        sourceData.allergies.map((allergy) => {
          const { id, name, typeAllergy } = allergy

          return (
            <Flex key={id} alignItems={'flex-start'} flexDir={'column'}>
              <Flex gap={'4'} w={'full'}>
                <FormControl>
                  <FormLabel>Tipo de alergia</FormLabel>
                  <Flex alignItems={'center'} gap={'4'}>
                    <CustomSelect
                      disabled
                      value={typeAllergy}
                      options={[
                        {
                          label: 'Medicamento',
                          value: TypesAllergy.enum.REMEDY
                        },
                        {
                          label: 'Alimento',
                          value: TypesAllergy.enum.FOOD
                        },
                        { label: 'Outros', value: TypesAllergy.enum.OTHERS }
                      ]}
                    />
                  </Flex>
                </FormControl>
                <FormControl>
                  <FormLabel>Descrição</FormLabel>
                  <Input
                    isDisabled
                    value={name}
                    border={'solid 1px'}
                    borderColor={name ? 'custom.gray.400' : 'custom.gray.600'}
                  />
                </FormControl>
              </Flex>
              <ReviewDataCheckboxArrayValue
                disabled={mode === MedicalRecordReviewType.enum.VIEW}
                checkboxProps={{
                  mt: '4'
                }}
                parallelValues={comparisonData.allergies}
                currentValue={allergy}
                formValue={allergies}
                set={(v) => allergiesFieldArray.append(v)}
                reset={() =>
                  allergiesFieldArray.remove(
                    allergiesFieldArray.fields.findIndex(
                      (item) =>
                        item.name === allergy.name &&
                        item.typeAllergy === allergy.typeAllergy
                    ) ?? -1
                  )
                }
                comparison={(a, b) =>
                  a.name === b.name && a.typeAllergy === b.typeAllergy
                }
              />
            </Flex>
          )
        })
      )}
    </ReviewStepWrapper>
  )
}
